import {Button, Spinner} from "react-bootstrap";
import XLSX from "xlsx";
import FileSaver from "file-saver";
import {useTranslation} from "react-i18next";
import {TableHeaders} from "../utils/Enums/TableHeaders";
import {InvoiceFilter} from "../models/table-models";
import {transformDateToString, transformPriceToString} from "../utils/helper-functions";
import {
    getInvoicesPDF,
    useGetInvoices, useGetSales
} from "../utils/clients-hooks";
import {Pagination} from "../utils/pagination-hooks";
import {toast} from "react-toastify";
import {CustomToastContent} from "../components/CustomToastContent";
import {toastOptionsTopRight} from "../utils/Enums/ToastOptions";
import React, {useState} from "react";

export const ExportSales = ({pagination, filter, fileName, wscols}: {
    pagination: Pagination,
    filter: InvoiceFilter,
    fileName: string,
    wscols: TableHeaders[]
}) => {
    const {t} = useTranslation('translation', {keyPrefix: 'table'});
    const {t: common} = useTranslation('translation', {keyPrefix: 'common'});
    const [loading, setLoading] = useState(false);
    const [loadingExcel, setLoadingExcel] = useState(false);

    const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";

    const headers = [...wscols.map((col) => t(col))];

    const calculateColumnWidths = (headers: string[], data: any[][]) => {
        return headers.map((header, colIndex) => {
            // Get max content length in column (including header)
            const maxContentWidth = Math.max(
                header.length,
                ...data.map(row => (row[colIndex] ? row[colIndex].toString().length : 0)) // Data length
            );
            return {wch: Math.max(10, maxContentWidth + 2)}; // Ensure min width of 10
        });
    };

    const useExportToCSV = async () => {
        setLoadingExcel(true);

        const fetchInvoices = await useGetSales({
            'pager.page': 1,
            'pager.itemsPerPage': 1000000,
            pagination,
            filter,
        }), csvData = fetchInvoices.paidInvoices;

        const ws = XLSX.utils.aoa_to_sheet([headers]);

        const formattedData = csvData.map((item) => [
            item.document?.invoiceNumber,
            transformDateToString(item.creationDate),
            common(item.documentType.toLowerCase()),
            item.documentType.toLowerCase()=="receipt" ? t("privatePerson") : item.document?.clientName ?? item.companyName,
            item.documentType.toLowerCase()=="receipt" ? "" : item.document?.eik ?? item.eik,
            item.user,
            transformPriceToString(item.total),
            common(item.paymentMethod.toLowerCase())
        ]);

        XLSX.utils.sheet_add_json(ws, formattedData, {
            origin: "A2", // Ensure data starts from row 2
            skipHeader: true // Headers are already added
        });

        ws["!cols"] = calculateColumnWidths(headers, formattedData);

        const wb = {Sheets: {data: ws}, SheetNames: ["data"]};
        const excelBuffer = XLSX.write(wb, {bookType: "xlsx", type: "array"});
        const data = new Blob([excelBuffer], {type: fileType});
        FileSaver.saveAs(data, fileName + fileExtension);

        setLoadingExcel(false);
    };

    const usePrint = async () => {
        setLoading(true);
        const fetchInvoices = await useGetInvoices({
            'pager.page': 1,
            'pager.itemsPerPage': 1000000,
            pagination,
            filter,
        }), csvData = fetchInvoices.paidInvoices;

        if (csvData.length > 0) {
            const pdfFile = await getInvoicesPDF(csvData.map((item) => item.id).filter((id): id is number => id !== undefined));
            if (pdfFile) {
                const pdf = URL.createObjectURL(pdfFile);
                window.open(pdf, '_blank');
                setLoading(false);
                return true;
            } else {
                toast.error(<CustomToastContent content={[`${t('printError')}`]} whiteText/>, toastOptionsTopRight);
                setLoading(false);
                return false;
            }
        }
        setLoading(false);
    };

    return (
        <div className={"d-flex"}>
            {loadingExcel ? <Spinner animation={'border'}></Spinner>
                :
                <Button
                    variant="warning"
                    className={"mb-2"}
                    onClick={useExportToCSV}
                >
                    {common('exportExcel')}
                </Button>}
            {/*{loading ? <Spinner animation={'border'}></Spinner>*/}
            {/*    :*/}
            {/*    <Button*/}
            {/*        variant="warning"*/}
            {/*        className={"mb-2"}*/}
            {/*        onClick={usePrint}*/}
            {/*    >*/}
            {/*        {common('printAllInvoices')}*/}
            {/*    </Button>}*/}
        </div>
    );
};